import { Ref, useCallback, useImperativeHandle, useMemo } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
  safeGetByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import SelectListItemWithDetails, {
  ListItemWithDetails,
  TagItem,
} from "@coworker/apprestructured/src/task-management/components/SelectListItemWithDetails/SelectListItemWithDetails";
import { useTranslation } from "@coworker/locales";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";

export type SelectListItemWithDetailsCreatorFragmentConfigProps = {
  // Translation key for the title
  title: string;
  field: string;
  descriptionField?: string;
  tagsField?: string;
  maxLength?: number;
  items: ListItemWithDetails[];
  visibility?: VisibilityCondition;
};

type SelectListItemWithDetailsCreatorFragmentProps = {
  configProps: SelectListItemWithDetailsCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

type ListItemDetailsValue = {
  value: string;
  // Translation key for the label
  label: string;
  description: string;
  tags: TagItem[];
};

function SelectListItemWithDetailsCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectListItemWithDetailsCreatorFragmentProps) {
  const {
    field,
    descriptionField,
    tagsField,
    title,
    items,
    maxLength,
    visibility,
  } = configProps;
  const { blockInput, task, onTaskChange, showValidation } = commonProps;
  const { t } = useTranslation();

  const listItem = getTypedValue<ListItemDetailsValue>(getByPath(task, field));
  const rawDescription = descriptionField
    ? safeGetByPath(task, descriptionField)
    : undefined;
  const rawTags = tagsField ? safeGetByPath(task, tagsField) : undefined;

  const descriptionValue = descriptionField
    ? typeof rawDescription === "string"
      ? rawDescription
      : ""
    : !isLoading(listItem)
      ? (listItem?.description ?? "")
      : "";
  const tagsValue = tagsField
    ? Array.isArray(rawTags)
      ? rawTags
      : []
    : !isLoading(listItem)
      ? (listItem?.tags ?? [])
      : [];

  const handleConfirmClick = useCallback(
    (newItem: {
      value: string;
      label: string;
      description: string;
      tags: TagItem[];
    }) => {
      const patch = setByPath(field, {
        value: newItem.value,
        label: newItem.label,
        ...(!descriptionField && { description: newItem.description }),
        ...(!tagsField && { tags: newItem.tags }),
      });
      const patchWithDescription = descriptionField
        ? setByPath(descriptionField, newItem.description, patch)
        : patch;
      const patchWithTags = tagsField
        ? setByPath(tagsField, newItem.tags, patchWithDescription)
        : patchWithDescription;

      onTaskChange(patchWithTags);
    },
    [descriptionField, field, onTaskChange, tagsField]
  );

  const selectedItem =
    !isLoading(listItem) && listItem != null
      ? items.find((item) => item.value === listItem.value)
      : undefined;
  const requireDescription = selectedItem?.requireDescription ?? false;

  const valid =
    !isLoading(listItem) &&
    listItem != null &&
    (listItem?.value ?? "") !== "" &&
    (!requireDescription || descriptionValue.trim() !== "") &&
    (maxLength === undefined || descriptionValue.length <= maxLength);

  const errorMessage = useMemo(() => {
    if (!showValidation || valid) return undefined;
    if (
      listItem != null &&
      !isLoading(listItem) &&
      maxLength !== undefined &&
      descriptionValue.length > maxLength
    ) {
      return t("maximumCharactersLengthString", { count: maxLength });
    }
    return t("pleaseFillInString");
  }, [showValidation, valid, descriptionValue, maxLength, t]);

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (visibility && !isLoading(task) && !shouldShowField(task, visibility)) {
    return null;
  }

  if (isLoading(listItem)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectListItemWithDetails
      title={t(title)}
      label={listItem?.label ?? ""}
      description={descriptionValue}
      value={listItem?.value ?? ""}
      selectedTags={tagsValue}
      listItems={items}
      maxLength={maxLength}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      errorMessage={errorMessage}
    />
  );
}

export default SelectListItemWithDetailsCreatorFragment;
